/**
 * Pure logic utilities for the Article Reviewer Progress Report.
 * These functions are extracted for testability and separation of concerns.
 */

// ── Constants ─────────────────────────────────────────────────────────────────

export const BOOKLET_SECTIONS = [
  'Front Matter',
  'Feature Stories',
  'Heritage & History',
  'Faith & Culture',
  'People & Community',
  'Culture & Lifestyle',
  'KDU Global Impact',
  'Looking Forward',
] as const;

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface ReviewProgressArticle {
  id: string;
  title: string;
  bookletSection: string | null;
  status: string;
  authorName?: string;
  reviewers: {
    assignmentId: string;
    memberId: string;
    firstName: string;
    lastName: string;
    userId?: string | null;
    reviewed: boolean;
    reviewedAt: string | null;
    assignedAt: string;
    commentCount: number;
  }[];
}

export interface UniqueReviewer {
  memberId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  fullName: string;
}

export type CellStatus =
  | { type: 'completed'; reviewedAt: string }
  | { type: 'pending'; assignedAt: string }
  | { type: 'none' };

export interface SummaryStats {
  totalArticles: number;
  completedReviews: number;
  totalAssignments: number;
  percentage: number;
}

// ── Logic Functions ───────────────────────────────────────────────────────────

/**
 * Derive unique reviewer list from all articles, format display names as
 * "FirstName L.", disambiguate collisions, sort by lastName then firstName.
 */
export function buildReviewerColumns(articles: ReviewProgressArticle[]): UniqueReviewer[] {
  // Collect unique reviewers by memberId
  const reviewerMap = new Map<string, { memberId: string; firstName: string; lastName: string }>();

  for (const article of articles) {
    for (const reviewer of article.reviewers) {
      if (!reviewerMap.has(reviewer.memberId)) {
        reviewerMap.set(reviewer.memberId, {
          memberId: reviewer.memberId,
          firstName: reviewer.firstName,
          lastName: reviewer.lastName,
        });
      }
    }
  }

  const reviewers = Array.from(reviewerMap.values());

  // Sort by lastName then firstName
  reviewers.sort((a, b) => {
    const lastCmp = a.lastName.localeCompare(b.lastName);
    if (lastCmp !== 0) return lastCmp;
    return a.firstName.localeCompare(b.firstName);
  });

  // Format display names and disambiguate collisions
  const result: UniqueReviewer[] = reviewers.map((r) => ({
    memberId: r.memberId,
    firstName: r.firstName,
    lastName: r.lastName,
    displayName: '', // will be set below
    fullName: `${r.firstName} ${r.lastName}`,
  }));

  // Build initial display names as "FirstName L."
  const displayNames = reviewers.map((r) => `${r.firstName} ${r.lastName.charAt(0)}.`);

  // Disambiguate collisions by appending more of the last name
  let hasCollision = true;
  while (hasCollision) {
    hasCollision = false;
    for (let i = 0; i < displayNames.length; i++) {
      const collisionIndices: number[] = [];
      for (let j = 0; j < displayNames.length; j++) {
        if (displayNames[j] === displayNames[i]) {
          collisionIndices.push(j);
        }
      }
      if (collisionIndices.length > 1) {
        hasCollision = true;
        for (const idx of collisionIndices) {
          const reviewer = reviewers[idx];
          // Determine how many chars of lastName are currently shown
          // Display format is "FirstName X." where X is last name prefix
          const prefix = `${reviewer.firstName} `;
          const currentSuffix = displayNames[idx].slice(prefix.length, -1); // remove trailing "."
          const nextCharCount = currentSuffix.length + 1;
          if (nextCharCount <= reviewer.lastName.length) {
            displayNames[idx] = `${reviewer.firstName} ${reviewer.lastName.slice(0, nextCharCount)}.`;
          } else {
            // Use full last name (no trailing dot if we've exhausted it)
            displayNames[idx] = `${reviewer.firstName} ${reviewer.lastName}`;
          }
        }
      }
    }
  }

  // Assign display names
  for (let i = 0; i < result.length; i++) {
    result[i].displayName = displayNames[i];
  }

  return result;
}

/**
 * Determine the cell status for a given article-reviewer pair.
 */
export function computeCellStatus(article: ReviewProgressArticle, reviewerId: string): CellStatus {
  const assignment = article.reviewers.find((r) => r.memberId === reviewerId);
  if (!assignment) {
    return { type: 'none' };
  }
  if (assignment.reviewed && assignment.reviewedAt) {
    return { type: 'completed', reviewedAt: assignment.reviewedAt };
  }
  return { type: 'pending', assignedAt: assignment.assignedAt };
}

/**
 * Map a bookletSection value to its BOOKLET_SECTIONS label or "Unassigned".
 */
export function resolveSectionLabel(bookletSection: string | null): string {
  if (!bookletSection) return 'Unassigned';
  const found = BOOKLET_SECTIONS.find((s) => s === bookletSection);
  return found || 'Unassigned';
}

/**
 * Calculate summary statistics for a set of articles.
 */
export function computeSummaryStats(articles: ReviewProgressArticle[]): SummaryStats {
  const totalArticles = articles.length;
  let completedReviews = 0;
  let totalAssignments = 0;

  for (const article of articles) {
    for (const reviewer of article.reviewers) {
      totalAssignments++;
      if (reviewer.reviewed) {
        completedReviews++;
      }
    }
  }

  const percentage = totalAssignments === 0 ? 0 : Math.round((completedReviews / totalAssignments) * 100);

  return { totalArticles, completedReviews, totalAssignments, percentage };
}

/**
 * Filter articles by bookletSection. Returns all when section is 'all'.
 */
export function applyFilter(articles: ReviewProgressArticle[], section: string): ReviewProgressArticle[] {
  if (section === 'all') return articles;
  return articles.filter((a) => a.bookletSection === section);
}

/**
 * Sort articles by completion percentage ascending (reviewed / total per article).
 */
export function sortByCompletion(articles: ReviewProgressArticle[]): ReviewProgressArticle[] {
  return [...articles].sort((a, b) => {
    const pctA = a.reviewers.length === 0 ? 0 : a.reviewers.filter((r) => r.reviewed).length / a.reviewers.length;
    const pctB = b.reviewers.length === 0 ? 0 : b.reviewers.filter((r) => r.reviewed).length / b.reviewers.length;
    return pctA - pctB;
  });
}

/**
 * Compute total comments across all reviewers for an article.
 */
export function computeTotalComments(article: ReviewProgressArticle): number {
  return article.reviewers.reduce((sum, r) => sum + r.commentCount, 0);
}

/**
 * Format tooltip text for a review assignment.
 * Completed: "Reviewed: M/D/YYYY h:mm AM/PM"
 * Pending: "Assigned: M/D/YYYY"
 *
 * Uses new Date() for locale formatting since these are true UTC action timestamps.
 */
export function formatTooltip(assignment: { reviewed: boolean; reviewedAt: string | null; assignedAt: string }): string {
  if (assignment.reviewed && assignment.reviewedAt) {
    const date = new Date(assignment.reviewedAt);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `Reviewed: ${month}/${day}/${year} ${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  const date = new Date(assignment.assignedAt);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `Assigned: ${month}/${day}/${year}`;
}
